import ActionButton from "@/components/buttons/ActionButton";
import ActionTextIcon from "@/components/icons/ActionTextIcon";
import AntIcon from "@/components/icons/AntIcon";
import FileTypeIconWithFilename from "@/components/icons/FileTypeIconWithFilename";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import { UploadDragger } from "@/components/upload-dragger/UploadDragger";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatFileSize, formatLocaleDateTime } from "@/utils/formatUtils";
import { Dropdown, Popconfirm, Table, Tooltip } from "antd";
import { ColumnsType } from "antd/lib/table";
import { RcFile } from "antd/lib/upload";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router";
import {
  useDeleteClientAttachment,
  useDownloadClientAttachmentsAsZip,
  useGetClientAttachments,
  useMarkClientAttachmentType,
  useUploadClientAttachments,
} from "../../../../common/api/queries";
import { PageSizes, TableSizes } from "../../../../common/constants";
import { Permission } from "../../../../common/security/enums";
import { UUID } from "../../../../typings/global";
import { useHasPermissions } from "../../../auth/hooks";
import { ClientAttachmentType } from "../../enums";
import { CLIENT_ROUTE_PATHS } from "../../paths";
import { ClientAttachment } from "../../types";

interface Props {
  clientId: UUID;
}

export const ClientAttachmentsTable = ({ clientId }: Props) => {
  const { t } = useTranslation();

  const hasClientUpdatePermission = useHasPermissions(Permission.CLIENT_UPDATE);

  const { data: clientAttachments, isFetching } = useGetClientAttachments(clientId);
  const downloadClientAttachmentsAsZip = useDownloadClientAttachmentsAsZip();

  const uploadClientAttachments = useUploadClientAttachments();
  const deleteClientAttachment = useDeleteClientAttachment();
  const markClientAttachmentType = useMarkClientAttachmentType();

  const handleAttachmentsUpload = (_: RcFile, fileList: RcFile[]): boolean => {
    if (fileList.length > 0) {
      const formData = new FormData();
      fileList.forEach((file) => formData.append("files", file));
      fileList.splice(0, fileList.length);

      uploadClientAttachments.mutate({ id: clientId, object: formData });
    }

    return false;
  };

  const columns: ColumnsType<ClientAttachment> = [
    {
      key: "createdAt",
      title: t("common.createdAt"),
      width: 160,
      render: (_, record) => (record.createdAt ? formatLocaleDateTime(record.createdAt) : undefined),
    },
    {
      key: "type",
      width: 45,
      align: "center",
      render: (_, record) => {
        if (record.type === ClientAttachmentType.BACK_ID_CARD) {
          return (
            <Tooltip title={t("client.helpers.backSideIdentityCard")}>
              <span>
                <AntIcon type="pic-right" />
              </span>
            </Tooltip>
          );
        } else if (record.type === ClientAttachmentType.FRONT_ID_CARD) {
          return (
            <Tooltip title={t("client.helpers.frontSideIdentityCard")}>
              <span>
                <AntIcon type="pic-left" />
              </span>
            </Tooltip>
          );
        }

        return;
      },
    },
    {
      key: "filename",
      title: t("common.filename"),
      ellipsis: { showTitle: false },
      render: (_, record) => (
        <FileTypeIconWithFilename contentType={record.file.contentType} filename={record.file.filename} ellipsis />
      ),
    },
    {
      key: "size",
      title: t("common.filesize"),
      width: 100,
      render: (_, record) => formatFileSize(record.file.size),
    },
    {
      key: "actions",
      align: "right",
      width: 180,
      fixed: "right",
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "open",
                label: (
                  <ActionTextIcon
                    path={generatePath(CLIENT_ROUTE_PATHS.attachment.to, { id1: clientId, id2: record.id })}
                    target="_blank"
                    icon="export"
                    color="blue"
                    text={t("common.open")}
                  />
                ),
              },
              ...(hasClientUpdatePermission
                ? [
                    {
                      key: "frontSide",
                      label: (
                        <ActionTextIcon
                          icon="pic-left"
                          color="green"
                          text={t("client.actions.frontSideMark")}
                          onClick={() =>
                            markClientAttachmentType.mutate({
                              id1: clientId,
                              id2: record.id,
                              object: {
                                optimisticLockVersion: record.optimisticLockVersion,
                                type: ClientAttachmentType.FRONT_ID_CARD,
                              },
                            })
                          }
                        />
                      ),
                    },
                    {
                      key: "backSide",
                      label: (
                        <ActionTextIcon
                          icon="pic-right"
                          color="green"
                          text={t("client.actions.backSideMark")}
                          onClick={() =>
                            markClientAttachmentType.mutate({
                              id1: clientId,
                              id2: record.id,
                              object: {
                                optimisticLockVersion: record.optimisticLockVersion,
                                type: ClientAttachmentType.BACK_ID_CARD,
                              },
                            })
                          }
                        />
                      ),
                    },
                    {
                      key: "delete",
                      label: (
                        <Popconfirm
                          title={t("common.deleteAttachmentConfirm")}
                          icon={<PopconfirmDeleteIcon />}
                          okText={t("common.yes")}
                          cancelText={t("common.no")}
                          okType="danger"
                          onConfirm={() => deleteClientAttachment.mutate({ id1: clientId, id2: record.id })}
                        >
                          <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
                        </Popconfirm>
                      ),
                    },
                  ]
                : [null]),
            ],
          }}
          trigger={["click"]}
        >
          <span>
            <ActionTextIcon icon="menu-unfold" color="blue" text={t("common.options")} />
          </span>
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      {clientAttachments?.length ? (
        <div style={{ position: "absolute", zIndex: 1, top: 15 }}>
          <ActionButton icon="download" onClick={() => downloadClientAttachmentsAsZip.mutate(clientId)}>
            {t("client.actions.downloadAttachmentsAsZip")}
          </ActionButton>
        </div>
      ) : undefined}
      <Table<ClientAttachment>
        {...tableStandardProps()}
        columns={columns}
        loading={isFetching}
        scroll={{ x: TableSizes.SMALL }}
        dataSource={clientAttachments}
        pagination={{ ...paginationTableProps, pageSize: PageSizes.LARGE, total: clientAttachments?.length }}
      />

      {hasClientUpdatePermission ? (
        <div className="margin-top-medium margin-bottom-small">
          <UploadDragger multiple showUploadList={false} beforeUpload={handleAttachmentsUpload} />
        </div>
      ) : undefined}
    </div>
  );
};
